import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Star, EyeOff, Check, Pencil, Trash2, User } from "lucide-react";

const FILTERS = [["all", "Tous"], ["pending", "À valider"], ["published", "Publiés"], ["hidden", "Masqués"]];
const STATUS_UI = { pending: ["À valider", "bg-amber-500/15 text-amber-300"], published: ["Publié", "bg-emerald-500/15 text-emerald-300"], hidden: ["Masqué", "bg-zinc-700/40 text-zinc-400"] };
const EMPTY = { author_name: "", company: "", role: "", rating: 5, text: "", photo: "", featured: false, status: "published" };

export function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} data-testid={testid ? `${testid}-${n}` : undefined} onClick={() => onChange?.(n)} className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}>
          <Star className={`h-4 w-4 ${n <= value ? "text-[#FFB800]" : "text-zinc-700"}`} fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

const resizePhoto = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => {
    const img = new Image();
    img.onload = () => {
      const s = 256 / Math.max(img.width, img.height, 256);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = r.result;
  };
  r.readAsDataURL(file);
});

function ReviewDialog({ review, onClose, onSaved, h }) {
  const [f, setF] = useState(review);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      if (f.id) await axios.put(`${API}/superadmin/site/reviews/${f.id}`, f, h);
      else await axios.post(`${API}/superadmin/site/reviews`, f, h);
      toast.success("Avis enregistré"); onSaved();
    } catch (e) { const d = e.response?.data?.detail; toast.error(Array.isArray(d) ? "Nom et texte (10 caractères min.) requis" : d || "Erreur"); }
  };
  const pick = async (file) => { if (file) { try { set("photo", await resizePhoto(file)); } catch { toast.error("Image illisible"); } } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="site-review-dialog" className="max-w-lg border-zinc-800 bg-zinc-950 text-white">
        <DialogHeader><DialogTitle>{f.id ? "Modifier l'avis" : "Nouvel avis"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {f.photo ? <img src={f.photo} alt="" className="h-14 w-14 rounded-full object-cover" /> : <span className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-800 text-zinc-500"><User className="h-6 w-6" /></span>}
            <label className="cursor-pointer rounded-full border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 hover:border-[#FF6A00]/60">Choisir une photo<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="site-review-photo" onChange={(e) => pick(e.target.files?.[0])} /></label>
            {f.photo && <button type="button" onClick={() => set("photo", "")} className="text-xs text-red-400">Retirer</button>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-zinc-400">Nom</Label><Input data-testid="site-review-name" value={f.author_name} onChange={(e) => set("author_name", e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900" /></div>
            <div><Label className="text-zinc-400">Entreprise</Label><Input data-testid="site-review-company" value={f.company} onChange={(e) => set("company", e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900" /></div>
            <div><Label className="text-zinc-400">Fonction</Label><Input data-testid="site-review-role" value={f.role} onChange={(e) => set("role", e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900" /></div>
            <div><Label className="text-zinc-400">Note</Label><div className="mt-2"><Stars value={f.rating} onChange={(n) => set("rating", n)} testid="site-review-rating" /></div></div>
          </div>
          <div><Label className="text-zinc-400">Avis</Label><Textarea rows={4} data-testid="site-review-text" value={f.text} onChange={(e) => set("text", e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900" /></div>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="site-review-featured" checked={!!f.featured} onCheckedChange={(v) => set("featured", v)} />Mettre en avant (affiché en premier)</label>
          <Button data-testid="site-review-save" onClick={save} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewRow({ r, readOnly, onStatus, onEdit, onDelete }) {
  const [label, cls] = STATUS_UI[r.status] || STATUS_UI.pending;
  return (
    <div data-testid={`site-review-${r.id}`} className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4 sm:flex-row sm:items-start">
      {r.photo ? <img src={r.photo} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" /> : <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-zinc-500"><User className="h-5 w-5" /></span>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-white">{r.author_name}</span>
          <span className="text-xs text-zinc-500">{[r.role, r.company].filter(Boolean).join(" · ")}</span>
          <span data-testid={`site-review-${r.id}-status`} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>
          {r.featured && <span className="rounded-full bg-[#FF6A00]/15 px-2 py-0.5 text-[11px] text-[#FF6A00]">En avant</span>}
          <span className="text-[11px] text-zinc-600">{r.source === "app" ? `Déposé depuis l'app${r.user_email ? ` · ${r.user_email}` : ""}` : "Saisi dans Valhalla"}</span>
        </div>
        <div className="mt-1"><Stars value={r.rating} /></div>
        <p className="mt-1.5 whitespace-pre-line text-sm text-zinc-300">{r.text}</p>
      </div>
      {!readOnly && (
        <div className="flex shrink-0 gap-1">
          {r.status !== "published" && <button data-testid={`site-review-${r.id}-publish`} title="Publier" onClick={() => onStatus("published")} className="rounded-full p-2 text-emerald-400 hover:bg-emerald-500/10"><Check className="h-4 w-4" /></button>}
          {r.status === "published" && <button data-testid={`site-review-${r.id}-hide`} title="Masquer" onClick={() => onStatus("hidden")} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-800"><EyeOff className="h-4 w-4" /></button>}
          <button data-testid={`site-review-${r.id}-edit`} title="Modifier" onClick={onEdit} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-800"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`site-review-${r.id}-delete`} title="Supprimer" onClick={onDelete} className="rounded-full p-2 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

export default function SiteReviews({ token, readOnly }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const load = () => axios.get(`${API}/superadmin/site/reviews`, h).then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => { try { await axios.patch(`${API}/superadmin/site/reviews/${id}/status`, { status }, h); load(); } catch { toast.error("Erreur"); } };
  const remove = async (id) => { if (!window.confirm("Supprimer cet avis ?")) return; await axios.delete(`${API}/superadmin/site/reviews/${id}`, h); load(); };
  const shown = filter === "all" ? list : list.filter((r) => r.status === filter);
  const pending = list.filter((r) => r.status === "pending").length;
  return (
    <section className="space-y-4" data-testid="site-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-zinc-300">Avis clients {pending > 0 && <span data-testid="site-reviews-pending" className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300">{pending} à valider</span>}</h3>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => <button key={k} data-testid={`site-reviews-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "bg-[#FF6A00]/15 text-[#FF6A00]" : "text-zinc-400 hover:bg-zinc-800"}`}>{l}</button>)}
          {!readOnly && <Button data-testid="site-review-add" onClick={() => setEdit(EMPTY)} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Ajouter un avis</Button>}
        </div>
      </div>
      <div className="space-y-2">
        {shown.length === 0 ? <p className="text-sm text-zinc-500" data-testid="site-reviews-empty">Aucun avis.</p> : shown.map((r) => (
          <ReviewRow key={r.id} r={r} readOnly={readOnly} onStatus={(s) => setStatus(r.id, s)} onEdit={() => setEdit(r)} onDelete={() => remove(r.id)} />
        ))}
      </div>
      {edit && <ReviewDialog review={edit} h={h} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </section>
  );
}
